import { useState, useEffect } from "react";
import { T, STATE, canAccess } from "../../config/theme.js";
import { api } from "../../hooks/useApi.js";
import { Card, Bar, SectionTitle } from "../ui/index.jsx";
import TrendCharts from "./TrendCharts.jsx";
import { Lock, Activity, BarChart2, DollarSign, Layers, Target, TrendingDown, TrendingUp, Info, AlertTriangle } from "lucide-react";

/* ── 간단한 미니 바 차트 (recharts 없이 순수 div) ── */
function FinancialMiniChart({ data, plan }) {
  // Free: 최근 2분기, Starter+: 전체
  const visible = plan === "free" ? data.slice(-2) : data;
  const allValid = visible.filter(d => d.revenue || d.operating || d.net);
  if (allValid.length === 0) return <div style={{ textAlign: "center", padding: 20, color: T.textDim, fontSize: 13 }}>표시할 재무 데이터가 없습니다</div>;

  const maxVal = Math.max(...allValid.map(d => Math.max(d.revenue || 0, d.operating || 0, d.net || 0)));
  const barH = (v) => v ? Math.max(2, (v / maxVal) * 120) : 0;

  const LEGEND = [
    { label: "매출", color: T.accent },
    { label: "영업이익", color: T.green },
    { label: "순이익", color: T.yellow },
  ];

  return (
    <div>
      {/* 범례 */}
      <div style={{ display: "flex", gap: 14, marginBottom: 12 }}>
        {LEGEND.map(l => (
          <div key={l.label} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: l.color }} />
            <span style={{ fontSize: 12, color: T.textSub || T.textDim, fontWeight: 600 }}>{l.label}</span>
          </div>
        ))}
      </div>

      {/* 차트 영역 */}
      <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 150, padding: "0 4px" }}>
        {allValid.map((d, i) => (
          <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 120 }}>
              <div style={{ width: 10, height: barH(d.revenue), background: T.accent, borderRadius: "2px 2px 0 0", transition: "height 0.3s" }} title={`매출 ${(d.revenue || 0).toLocaleString()}억`} />
              <div style={{ width: 10, height: barH(d.operating), background: T.green, borderRadius: "2px 2px 0 0", transition: "height 0.3s" }} title={`영업이익 ${(d.operating || 0).toLocaleString()}억`} />
              <div style={{ width: 10, height: barH(d.net), background: T.yellow, borderRadius: "2px 2px 0 0", transition: "height 0.3s" }} title={`순이익 ${(d.net || 0).toLocaleString()}억`} />
            </div>
            <div style={{ fontSize: 13, color: T.textSub || T.textDim, marginTop: 4, fontWeight: 600 }}>{d.quarter}</div>
          </div>
        ))}
      </div>

      {/* 수치 테이블 */}
      <div style={{ marginTop: 16, overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${T.border}` }}>
              <th style={{ textAlign: "left", padding: "6px 8px", color: T.textSub || T.textDim, fontWeight: 600 }}>분기</th>
              <th style={{ textAlign: "right", padding: "6px 8px", color: T.accent, fontWeight: 600 }}>매출</th>
              <th style={{ textAlign: "right", padding: "6px 8px", color: T.green, fontWeight: 600 }}>영업이익</th>
              <th style={{ textAlign: "right", padding: "6px 8px", color: T.yellow, fontWeight: 600 }}>순이익</th>
            </tr>
          </thead>
          <tbody>
            {allValid.map((d, i) => (
              <tr key={i} style={{ borderBottom: i < allValid.length - 1 ? `1px solid ${T.border}` : "none" }}>
                <td style={{ padding: "6px 8px", color: T.text, fontWeight: 600 }}>{d.quarter}</td>
                <td style={{ padding: "6px 8px", color: T.text, textAlign: "right" }}>{d.revenue ? `${d.revenue.toLocaleString()}억` : "-"}</td>
                <td style={{ padding: "6px 8px", color: T.text, textAlign: "right" }}>{d.operating ? `${d.operating.toLocaleString()}억` : "-"}</td>
                <td style={{ padding: "6px 8px", color: T.text, textAlign: "right" }}>{d.net ? `${d.net.toLocaleString()}억` : "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Free 플랜 잠금 안내 */}
      {plan === "free" && data.length > 2 && (
        <div style={{ marginTop: 12, padding: "10px 14px", background: T.accent + "08", borderRadius: 8, fontSize: 12, color: T.textDim, textAlign: "center" }}>
          전체 분기 이력은 Starter 이상에서 확인할 수 있습니다
        </div>
      )}
    </div>
  );
}

/* ── 수급 동향 바 차트 (Sprint 15-1-A) ── */
function InvestorFlowChart({ data, plan }) {
  if (!data || data.length === 0) return null;

  // Free: 텍스트만, Starter: 금액, Core+: 차트
  const showChart = plan !== "free";
  const maxAbs = Math.max(...data.map(d => Math.max(Math.abs(d.foreign), Math.abs(d.inst))), 1);

  const barH = (v) => Math.max(1, (Math.abs(v) / maxAbs) * 60);

  // 최근 5일 합계
  const recent5 = data.slice(-5);
  const foreignSum = recent5.reduce((s, d) => s + d.foreign, 0);
  const instSum = recent5.reduce((s, d) => s + d.inst, 0);

  if (!showChart) {
    // Free: 텍스트 요약만
    return (
      <div>
        <div style={{ display: "flex", gap: 16, marginBottom: 8 }}>
          <div>
            <span style={{ fontSize: 13, color: T.textSub || T.textDim, fontWeight: 600 }}>외국인 5일 합계: </span>
            <span style={{ fontSize: 15, fontWeight: 800, color: foreignSum >= 0 ? T.blue || T.accent : T.red }}>{foreignSum >= 0 ? "+" : ""}{foreignSum.toLocaleString()}억</span>
          </div>
          <div>
            <span style={{ fontSize: 13, color: T.textSub || T.textDim, fontWeight: 600 }}>기관 5일 합계: </span>
            <span style={{ fontSize: 15, fontWeight: 800, color: instSum >= 0 ? T.red : T.red }}>{instSum >= 0 ? "+" : ""}{instSum.toLocaleString()}억</span>
          </div>
        </div>
        <div style={{ padding: "10px 14px", background: T.accent + "08", borderRadius: 8, fontSize: 12, color: T.textDim, textAlign: "center" }}>
          수급 차트는 Starter 이상에서 확인할 수 있습니다
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* 범례 + 5일 합계 */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", gap: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: T.blue || T.accent }} />
            <span style={{ fontSize: 12, color: T.textSub || T.textDim, fontWeight: 600 }}>외국인</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: T.red }} />
            <span style={{ fontSize: 12, color: T.textSub || T.textDim, fontWeight: 600 }}>기관</span>
          </div>
        </div>
        <div style={{ fontSize: 12, color: T.textDim }}>
          5일 합계: 외국인 <span style={{ color: foreignSum >= 0 ? T.blue || T.accent : T.red, fontWeight: 700 }}>{foreignSum >= 0 ? "+" : ""}{foreignSum.toLocaleString()}억</span>
          {" / "}기관 <span style={{ color: instSum >= 0 ? T.red : T.red, fontWeight: 700 }}>{instSum >= 0 ? "+" : ""}{instSum.toLocaleString()}억</span>
        </div>
      </div>

      {/* 양방향 바 차트 */}
      <div style={{ display: "flex", alignItems: "center", gap: 3, height: 140, padding: "0 4px" }}>
        {data.map((d, i) => (
          <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", height: "100%", justifyContent: "center" }}>
            {/* 위쪽: 양수 (매수) */}
            <div style={{ height: 60, display: "flex", flexDirection: "column", justifyContent: "flex-end", alignItems: "center", gap: 1 }}>
              {d.foreign > 0 && <div style={{ width: 8, height: barH(d.foreign), background: T.blue || T.accent, borderRadius: "2px 2px 0 0", transition: "height 0.3s" }} />}
              {d.inst > 0 && <div style={{ width: 8, height: barH(d.inst), background: T.red, borderRadius: "2px 2px 0 0", transition: "height 0.3s", opacity: 0.8 }} />}
            </div>
            {/* 0 라인 */}
            <div style={{ width: "100%", height: 1, background: T.border, margin: "2px 0" }} />
            {/* 아래쪽: 음수 (매도) */}
            <div style={{ height: 60, display: "flex", flexDirection: "column", justifyContent: "flex-start", alignItems: "center", gap: 1 }}>
              {d.foreign < 0 && <div style={{ width: 8, height: barH(d.foreign), background: T.blue || T.accent, borderRadius: "0 0 2px 2px", transition: "height 0.3s", opacity: 0.6 }} />}
              {d.inst < 0 && <div style={{ width: 8, height: barH(d.inst), background: T.red, borderRadius: "0 0 2px 2px", transition: "height 0.3s", opacity: 0.5 }} />}
            </div>
            {/* 날짜 */}
            <div style={{ fontSize: 10, color: T.textMuted, marginTop: 4 }}>{d.dateLabel}</div>
          </div>
        ))}
      </div>

      {/* 수치 테이블 */}
      <div style={{ marginTop: 12, overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${T.border}` }}>
              <th style={{ textAlign: "left", padding: "6px 8px", color: T.textSub || T.textDim, fontWeight: 600 }}>날짜</th>
              <th style={{ textAlign: "right", padding: "6px 8px", color: T.blue || T.accent, fontWeight: 600 }}>외국인</th>
              <th style={{ textAlign: "right", padding: "6px 8px", color: T.red, fontWeight: 600 }}>기관</th>
            </tr>
          </thead>
          <tbody>
            {data.slice(-7).reverse().map((d, i, arr) => (
              <tr key={i} style={{ borderBottom: i < arr.length - 1 ? `1px solid ${T.border}` : "none" }}>
                <td style={{ padding: "6px 8px", color: T.text, fontWeight: 600 }}>{d.dateLabel}</td>
                <td style={{ padding: "6px 8px", textAlign: "right", color: d.foreign >= 0 ? T.blue || T.accent : T.red, fontWeight: 600 }}>{d.foreign >= 0 ? "+" : ""}{d.foreign.toLocaleString()}억</td>
                <td style={{ padding: "6px 8px", textAlign: "right", color: d.inst >= 0 ? T.green : T.red, fontWeight: 600 }}>{d.inst >= 0 ? "+" : ""}{d.inst.toLocaleString()}억</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function DetailFundamentals({ holding, report, m, p, sc, plan, onUpgrade, token }) {
  /* ── 재무 데이터 fetch (실제 API 연동) ── */
  const [financials, setFinancials] = useState(null);
  const [finLoading, setFinLoading] = useState(true);

  useEffect(() => {
    if (!token || !holding.symbol) { setFinLoading(false); return; }
    setFinLoading(true);
    api(`/financials/${holding.symbol}?years=2`, {}, token)
      .then(data => {
        setFinancials(data.items || []);
      })
      .catch(e => {
        console.error("재무 데이터 로딩 실패:", e);
        setFinancials([]);
      })
      .finally(() => setFinLoading(false));
  }, [holding.symbol, token]);

  /* ── 수급 데이터 fetch (Sprint 15-1-A) ── */
  const [investor, setInvestor] = useState(null);
  const [invLoading, setInvLoading] = useState(true);

  useEffect(() => {
    if (!token || !holding.symbol) { setInvLoading(false); return; }
    setInvLoading(true);
    api(`/investor/${holding.symbol}?days=15`, {}, token)
      .then(data => setInvestor(data.items || []))
      .catch(e => {
        console.error("수급 데이터 로딩 실패:", e);
        setInvestor([]);
      })
      .finally(() => setInvLoading(false));
  }, [holding.symbol, token]);

  /* ── 기술 지표 fetch (Sprint 15-1-B) ── */
  const [tech, setTech] = useState(null);
  const [techLoading, setTechLoading] = useState(true);

  useEffect(() => {
    if (!token || !holding.symbol) { setTechLoading(false); return; }
    setTechLoading(true);
    api(`/tech/${holding.symbol}`, {}, token)
      .then(data => setTech(data))
      .catch(e => {
        console.error("기술지표 로딩 실패:", e);
        setTech(null);
      })
      .finally(() => setTechLoading(false));
  }, [holding.symbol, token]);

  /* ── 동종업계 비교 fetch (Sprint 15-2-A) ── */
  const [peers, setPeers] = useState(null);
  const [peersLoading, setPeersLoading] = useState(true);

  useEffect(() => {
    if (!token || !holding.symbol) { setPeersLoading(false); return; }
    if (!canAccess(plan, "sector_analysis")) { setPeersLoading(false); return; }
    setPeersLoading(true);
    api(`/peers/${holding.symbol}`, {}, token)
      .then(data => setPeers(data))
      .catch(e => {
        console.error("피어 비교 로딩 실패:", e);
        setPeers(null);
      })
      .finally(() => setPeersLoading(false));
  }, [holding.symbol, token, plan]);

  /* ── 배당 이력 fetch (Sprint 15-2-B) ── */
  const [dividendHistory, setDividendHistory] = useState(null);
  const [divLoading, setDivLoading] = useState(true);

  useEffect(() => {
    if (!token || !holding.symbol) { setDivLoading(false); return; }
    setDivLoading(true);
    api(`/dividend/${holding.symbol}?years=5`, {}, token)
      .then(data => setDividendHistory(data.items || []))
      .catch(e => {
        console.error("배당 이력 로딩 실패:", e);
        setDividendHistory([]);
      })
      .finally(() => setDivLoading(false));
  }, [holding.symbol, token]);

  return (
    <div>
      {/* Risk / Support + 세부 지표 (반응형 그리드) */}
      <div className="phase-detail-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 14, marginBottom: 16 }}>
        <Card>
          <SectionTitle icon="Activity" tip="Risk는 위험 신호 강도, Support는 안전 신호 강도입니다. Support가 높으면 안정적입니다">Risk / Support</SectionTitle>
          <Bar label="Risk (위험 점수)" value={p.risk || 0} color={T.red} sub="/100" />
          <Bar label="Support (안전 점수)" value={p.support || 0} color={T.green} sub="/100" />
          <div style={{ marginTop: 8, padding: "8px 12px", background: T.bg, borderRadius: 8, fontSize: 12, color: T.textDim }}>
            {p.risk > p.support ? "위험 신호가 더 강하게 나타나고 있습니다" : "안전 신호가 우위에 있습니다"}
          </div>
        </Card>
        <Card>
          <SectionTitle icon="BarChart2" tip="종목의 현재 상태를 여러 각도에서 분석한 세부 지표입니다">세부 지표</SectionTitle>
          {[
            { l: "수익률 (내 평균가 대비)", v: m.pnl_pct, u: "%", c: m.pnl_pct >= 0 ? T.green : T.red },
            { l: "모멘텀 (최근 흐름)", v: m.mom_5_20, u: "%", c: m.mom_5_20 >= 0 ? T.green : T.red },
            { l: "변동성 (가격 변동 폭)", v: m.atr_pct, u: "%", c: m.atr_pct >= 4 ? T.red : T.text },
            { l: "MDD (고점 대비 하락)", v: m.mdd_pct, u: "%", c: m.mdd_pct <= -10 ? T.red : T.text },
            { l: "52주 밴드 (1년 중 위치)", v: m.band_52w, u: "%", c: T.text },
            { l: "유동성 (거래량 수준)", v: m.liq_rel, u: "", c: m.liq_rel < 0.5 ? T.yellow : T.text },
            { l: "외국인 (연속 매매)", v: m.foreign_streak, u: "일", c: m.foreign_streak > 0 ? T.green : m.foreign_streak < 0 ? T.red : T.text },
            { l: "기관 (연속 매매)", v: m.inst_streak, u: "일", c: m.inst_streak > 0 ? T.green : m.inst_streak < 0 ? T.red : T.text },
          ].map((x, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: i < 7 ? `1px solid ${T.border}` : "none" }}>
              <span style={{ fontSize: 12, color: T.text, fontWeight: 600 }}>{x.l}</span>
              <span style={{ fontSize: 14, fontWeight: 800, color: x.c }}>
                {typeof x.v === "number" ? (x.u === "일" ? (x.v > 0 ? `연속 ${x.v}일 매수` : x.v < 0 ? `연속 ${Math.abs(x.v)}일 매도` : "중립") : x.v.toFixed(2)) : "-"}{x.u !== "일" ? x.u : ""}
              </span>
            </div>
          ))}
        </Card>
      </div>

      {/* 밸류에이션 */}
      {(m.per || m.pbr) && (
        <Card style={{ marginBottom: 14 }}>
          <SectionTitle icon="DollarSign" tip="이 종목이 비싼지 싼지를 숫자로 보여줍니다. PER이 낮으면 저평가 가능, 높으면 기대가 큰 것입니다">밸류에이션 (비싼지 싼지)</SectionTitle>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 10, marginBottom: 10 }}>
            {m.per != null && (
              <div style={{ padding: "12px 14px", background: T.bg, borderRadius: 8, textAlign: "center" }}>
                <div style={{ fontSize: 12, color: T.textSub || T.textDim, fontWeight: 600 }}>PER<span style={{ fontSize: 11, color: T.textMuted, fontWeight: 400 }}> (주가수익비율)</span></div>
                <div style={{ fontSize: 22, fontWeight: 900, color: m.per > 30 ? T.red : m.per > 15 ? T.yellow : m.per > 0 ? T.green : T.textMuted }}>{m.per > 0 ? m.per.toFixed(1) : "적자"}</div>
                <div style={{ fontSize: 12, color: T.textDim, fontWeight: 600 }}>{m.per > 30 ? "고평가 구간" : m.per > 15 ? "보통 수준" : m.per > 0 ? "저평가 가능" : "수익 없음"}</div>
              </div>
            )}
            {m.pbr != null && (
              <div style={{ padding: "12px 14px", background: T.bg, borderRadius: 8, textAlign: "center" }}>
                <div style={{ fontSize: 12, color: T.textSub || T.textDim, fontWeight: 600 }}>PBR<span style={{ fontSize: 11, color: T.textMuted, fontWeight: 400 }}> (주가순자산비율)</span></div>
                <div style={{ fontSize: 22, fontWeight: 900, color: m.pbr > 3 ? T.red : m.pbr > 1 ? T.text : T.green }}>{m.pbr ? m.pbr.toFixed(2) : "-"}</div>
                <div style={{ fontSize: 12, color: T.textDim, fontWeight: 600 }}>{m.pbr > 3 ? "자산 대비 고평가" : m.pbr > 1 ? "자산 대비 보통" : "자산 대비 저평가"}</div>
              </div>
            )}
            {m.eps != null && m.eps > 0 && (
              <div style={{ padding: "12px 14px", background: T.bg, borderRadius: 8, textAlign: "center" }}>
                <div style={{ fontSize: 12, color: T.textSub || T.textDim, fontWeight: 600 }}>EPS<span style={{ fontSize: 11, color: T.textMuted, fontWeight: 400 }}> (주당순이익)</span></div>
                <div style={{ fontSize: 22, fontWeight: 900, color: T.text }}>{m.eps?.toLocaleString()}원</div>
                <div style={{ fontSize: 12, color: T.textDim, fontWeight: 600 }}>1주당 벌어들이는 돈</div>
              </div>
            )}
            {m.dividend_yield != null && m.dividend_yield > 0 && (
              <div style={{ padding: "12px 14px", background: T.bg, borderRadius: 8, textAlign: "center" }}>
                <div style={{ fontSize: 13, color: T.textSub || T.textDim, fontWeight: 600 }}>배당수익률</div>
                <div style={{ fontSize: 22, fontWeight: 900, color: m.dividend_yield > 3 ? T.green : T.text }}>{m.dividend_yield.toFixed(2)}%</div>
                <div style={{ fontSize: 12, color: T.textDim, fontWeight: 600 }}>{m.dividend_yield > 3 ? "매력적 배당" : "보통 수준"}</div>
              </div>
            )}
          </div>
          <div style={{ fontSize: 12, color: T.textDim, padding: "8px 12px", background: T.bg, borderRadius: 8 }}>
            {m.per > 0 && m.per <= 10 ? `PER ${m.per.toFixed(1)}은(는) 시장 평균 대비 낮은 편입니다. 저평가 가능성이 있지만, 성장성도 함께 확인하세요.` :
             m.per > 30 ? `PER ${m.per.toFixed(1)}은(는) 높은 편입니다. 시장이 높은 성장을 기대하고 있지만, 기대에 못 미치면 하락 위험이 있습니다.` :
             m.per > 0 ? `PER ${m.per.toFixed(1)}은(는) 보통 수준입니다. 업종 평균과 비교하면 더 정확한 판단이 가능합니다.` :
             "현재 수익이 없어서(적자) PER을 계산할 수 없습니다."}
          </div>
        </Card>
      )}

      {/* 배당 이력 (Sprint 15-2-B) */}
      {m.dividend_yield != null && m.dividend_yield > 0 && (
        <Card style={{ marginBottom: 14 }}>
          <SectionTitle icon="DollarSign" tip="최근 3~5년간 주당 배당금과 배당 성향 추이를 보여줘요">배당 추이</SectionTitle>
          {divLoading ? (
            <div style={{ textAlign: "center", padding: 30, color: T.textDim, fontSize: 13 }}>배당 데이터를 불러오는 중입니다</div>
          ) : dividendHistory && dividendHistory.length > 0 ? (
            <div>
              {/* 배당금 미니 바 차트 */}
              <div style={{ display: "flex", alignItems: "flex-end", gap: 8, height: 120, padding: "0 4px", marginBottom: 12 }}>
                {dividendHistory.map((d, i) => {
                  const maxDps = Math.max(...dividendHistory.map(x => x.dps || 0), 1);
                  const h = d.dps ? Math.max(4, (d.dps / maxDps) * 100) : 0;
                  return (
                    <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
                      {d.dps ? (
                        <div style={{ fontSize: 11, color: T.text, fontWeight: 700, marginBottom: 4 }}>{d.dps.toLocaleString()}원</div>
                      ) : null}
                      <div style={{ width: 24, height: h, background: T.green, borderRadius: "4px 4px 0 0", transition: "height 0.3s" }} />
                      <div style={{ fontSize: 12, color: T.textSub || T.textDim, marginTop: 6, fontWeight: 600 }}>{d.year}</div>
                    </div>
                  );
                })}
              </div>

              {/* 배당 수치 테이블 */}
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr style={{ borderBottom: `2px solid ${T.border}` }}>
                      <th style={{ textAlign: "left", padding: "6px 8px", color: T.textSub || T.textDim, fontWeight: 600 }}>연도</th>
                      <th style={{ textAlign: "right", padding: "6px 8px", color: T.green, fontWeight: 600 }}>주당 배당금</th>
                      <th style={{ textAlign: "right", padding: "6px 8px", color: T.accent, fontWeight: 600 }}>배당 성향</th>
                      <th style={{ textAlign: "right", padding: "6px 8px", color: T.yellow, fontWeight: 600 }}>배당 수익률</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dividendHistory.map((d, i) => (
                      <tr key={i} style={{ borderBottom: `1px solid ${T.border}` }}>
                        <td style={{ padding: "6px 8px", color: T.text, fontWeight: 600 }}>{d.year}</td>
                        <td style={{ padding: "6px 8px", textAlign: "right", color: T.text, fontWeight: 600 }}>{d.dps ? `${d.dps.toLocaleString()}원` : "-"}</td>
                        <td style={{ padding: "6px 8px", textAlign: "right", color: T.text, fontWeight: 600 }}>{d.payout ? `${d.payout}%` : "-"}</td>
                        <td style={{ padding: "6px 8px", textAlign: "right", color: T.text, fontWeight: 600 }}>{d.dyield ? `${d.dyield}%` : "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ marginTop: 8, fontSize: 12, color: T.textMuted }}>출처: DART 전자공시 · 사업보고서 기준</div>
            </div>
          ) : (
            <div style={{ textAlign: "center", padding: 20, color: T.textDim, fontSize: 13 }}>배당 이력 데이터가 없습니다</div>
          )}
        </Card>
      )}

      {/* 동종업계 비교 (Sprint 15-2-A, Core+) */}
      {canAccess(plan, "sector_analysis") ? (
        <Card style={{ marginBottom: 14 }}>
          <SectionTitle icon="Layers" tip="같은 업종 시총 상위 종목들과 밸류에이션을 비교합니다">동종업계 비교</SectionTitle>
          {peersLoading ? (
            <div style={{ textAlign: "center", padding: 30, color: T.textDim, fontSize: 13 }}>비교 데이터를 불러오는 중입니다</div>
          ) : peers && peers.peers?.length > 0 ? (
            <div>
              {peers.sectorName && (
                <div style={{ fontSize: 13, color: T.accent, fontWeight: 600, marginBottom: 12 }}>{peers.sectorName}</div>
              )}
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: `2px solid ${T.border}` }}>
                      <th style={{ textAlign: "left", padding: "8px", color: T.textSub || T.textDim, fontWeight: 600 }}>종목</th>
                      <th style={{ textAlign: "right", padding: "8px", color: T.textSub || T.textDim, fontWeight: 600 }}>PER</th>
                      <th style={{ textAlign: "right", padding: "8px", color: T.textSub || T.textDim, fontWeight: 600 }}>PBR</th>
                      <th style={{ textAlign: "right", padding: "8px", color: T.textSub || T.textDim, fontWeight: 600 }}>배당률</th>
                    </tr>
                  </thead>
                  <tbody>
                    {peers.peers.map((p, i) => (
                      <tr key={i} style={{
                        borderBottom: `1px solid ${T.border}`,
                        background: p.isSelf ? T.accent + "08" : "transparent",
                      }}>
                        <td style={{ padding: "8px", color: p.isSelf ? T.accent : T.text, fontWeight: p.isSelf ? 800 : 600 }}>
                          {p.isSelf && "▸ "}{p.name}
                        </td>
                        <td style={{ padding: "8px", textAlign: "right", color: T.text, fontWeight: 600 }}>{p.per ? p.per.toFixed(1) : "-"}</td>
                        <td style={{ padding: "8px", textAlign: "right", color: T.text, fontWeight: 600 }}>{p.pbr ? p.pbr.toFixed(2) : "-"}</td>
                        <td style={{ padding: "8px", textAlign: "right", color: p.dividendYield > 3 ? T.green : T.text, fontWeight: 600 }}>{p.dividendYield ? p.dividendYield.toFixed(2) + "%" : "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ marginTop: 8, fontSize: 12, color: T.textMuted }}>
                일 1회 갱신 · 같은 업종 시총 상위 기준
              </div>
            </div>
          ) : (
            <div style={{ textAlign: "center", padding: 30, color: T.textDim, fontSize: 13 }}>동종업계 비교 데이터가 없습니다</div>
          )}
        </Card>
      ) : (
        <Card style={{ marginBottom: 14, cursor: "pointer" }} onClick={() => onUpgrade?.("sector_analysis")}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0" }}>
            <Lock size={20} color={T.textMuted} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 800, color: T.text }}>동종업계 비교</div>
              <div style={{ fontSize: 11, color: T.textMuted }}>Core 플랜에서 같은 업종 종목과 밸류에이션을 비교할 수 있습니다</div>
            </div>
            <span style={{ fontSize: 10, padding: "3px 8px", background: T.accent + "20", color: T.accent, borderRadius: 4, fontWeight: 700, marginLeft: "auto" }}>CORE</span>
          </div>
        </Card>
      )}

      {/* 재무 미니차트 (Sprint 14-5) */}
      <Card style={{ marginBottom: 14 }}>
        <SectionTitle icon="BarChart2" tip="최근 분기별 매출, 영업이익, 순이익 추이를 보여줘요">분기별 재무 추이</SectionTitle>
        {finLoading ? (
          <div style={{ textAlign: "center", padding: 30, color: T.textDim, fontSize: 13 }}>재무 데이터를 불러오는 중입니다</div>
        ) : financials && financials.length > 0 ? (
          <FinancialMiniChart data={financials} plan={plan} />
        ) : (
          <div style={{ textAlign: "center", padding: 30, color: T.textDim, fontSize: 13 }}>재무 데이터가 없습니다</div>
        )}
        <div style={{ marginTop: 10, fontSize: 13, color: T.textSub || T.textDim }}>
          단위: 억원 · 출처: DART 전자공시 · 분기 1회 갱신
        </div>
      </Card>

      {/* 수급 동향 차트 (Sprint 15-1-A) */}
      <Card style={{ marginBottom: 14 }}>
        <SectionTitle icon="Activity" tip="외국인과 기관이 최근 매일 얼마나 사고 팔았는지 보여줘요. 파란색은 외국인, 빨간색은 기관입니다">수급 동향 (외국인·기관)</SectionTitle>
        {invLoading ? (
          <div style={{ textAlign: "center", padding: 30, color: T.textDim, fontSize: 13 }}>수급 데이터를 불러오는 중입니다</div>
        ) : investor && investor.length > 0 ? (
          <InvestorFlowChart data={investor} plan={plan} />
        ) : (
          <div style={{ textAlign: "center", padding: 30, color: T.textDim, fontSize: 13 }}>수급 데이터가 없습니다</div>
        )}
        <div style={{ marginTop: 10, fontSize: 13, color: T.textSub || T.textDim }}>
          단위: 억원 · 출처: 네이버 금융 · 장 마감 후 갱신
        </div>
      </Card>

      {/* 기술적 지표 (Sprint 15-1-B) */}
      <Card style={{ marginBottom: 14 }}>
        <SectionTitle icon="TrendingUp" tip="이평선 배열과 볼린저 밴드로 현재 기술적 위치를 보여줘요. 매수/매도 신호가 아니라 현재 위치 설명입니다">기술적 위치</SectionTitle>
        {techLoading ? (
          <div style={{ textAlign: "center", padding: 30, color: T.textDim, fontSize: 13 }}>기술지표를 불러오는 중입니다</div>
        ) : tech && tech.ma ? (
          <div>
            {/* 이평선 정배열 신호등 */}
            <div style={{ display: "flex", gap: 16, marginBottom: 16, flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 200, padding: "16px", background: T.bg, borderRadius: 12 }}>
                <div style={{ fontSize: 13, color: T.textSub || T.textDim, fontWeight: 600, marginBottom: 8 }}>이평선 배열</div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                  <div style={{
                    width: 16, height: 16, borderRadius: "50%",
                    background: tech.ma.signal === "green" ? T.green : tech.ma.signal === "red" ? T.red : T.yellow,
                    boxShadow: `0 0 8px ${tech.ma.signal === "green" ? T.green : tech.ma.signal === "red" ? T.red : T.yellow}60`,
                  }} />
                  <span style={{
                    fontSize: 15, fontWeight: 800,
                    color: tech.ma.signal === "green" ? T.green : tech.ma.signal === "red" ? T.red : T.yellow,
                  }}>{tech.ma.label}</span>
                </div>
                <div style={{ display: "flex", gap: 12, fontSize: 12, color: T.textDim }}>
                  <span>5일 <strong style={{ color: T.text }}>{tech.ma.ma5?.toLocaleString()}</strong></span>
                  <span>20일 <strong style={{ color: T.text }}>{tech.ma.ma20?.toLocaleString()}</strong></span>
                  <span>60일 <strong style={{ color: T.text }}>{tech.ma.ma60?.toLocaleString()}</strong></span>
                </div>
                {tech.ma.disparity20 != null && (
                  <div style={{ fontSize: 12, color: T.textDim, marginTop: 6 }}>
                    20일선 이격도: <span style={{ color: tech.ma.disparity20 >= 0 ? T.green : T.red, fontWeight: 700 }}>{tech.ma.disparity20 >= 0 ? "+" : ""}{tech.ma.disparity20}%</span>
                  </div>
                )}
              </div>

              {/* 볼린저 밴드 게이지 */}
              <div style={{ flex: 1, minWidth: 200, padding: "16px", background: T.bg, borderRadius: 12 }}>
                <div style={{ fontSize: 13, color: T.textSub || T.textDim, fontWeight: 600, marginBottom: 8 }}>볼린저 밴드 위치</div>
                <div style={{ fontSize: 15, fontWeight: 800, color: T.text, marginBottom: 8 }}>{tech.bollinger.label}</div>
                {/* 게이지 바 */}
                <div style={{ position: "relative", height: 12, background: `linear-gradient(to right, ${T.green}50, ${T.yellow}50, ${T.red}50)`, borderRadius: 6, marginBottom: 8 }}>
                  <div style={{
                    position: "absolute",
                    left: `${Math.max(0, Math.min(100, tech.bollinger.position))}%`,
                    top: -4, width: 20, height: 20, borderRadius: "50%",
                    background: T.text, border: `3px solid ${T.bg}`,
                    transform: "translateX(-50%)",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                  }} />
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: T.textMuted }}>
                  <span>하단 {tech.bollinger.lower?.toLocaleString()}</span>
                  <span style={{ fontWeight: 700, color: T.text }}>{tech.bollinger.position}%</span>
                  <span>상단 {tech.bollinger.upper?.toLocaleString()}</span>
                </div>
                {tech.bollinger.bandwidth != null && (
                  <div style={{ fontSize: 12, color: T.textDim, marginTop: 6 }}>
                    밴드폭: <span style={{ color: T.text, fontWeight: 700 }}>{tech.bollinger.bandwidth}%</span>
                    <span style={{ marginLeft: 4 }}>{tech.bollinger.bandwidth > 10 ? "(변동성 큼)" : tech.bollinger.bandwidth < 4 ? "(수렴 중)" : "(보통)"}</span>
                  </div>
                )}
              </div>
            </div>

            <div style={{ fontSize: 12, color: T.textMuted, padding: "8px 12px", background: T.bg, borderRadius: 8 }}>
              위 지표는 매수/매도 신호가 아니라, 현재 기술적 위치를 설명하는 참고 자료입니다.
            </div>
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: 30, color: T.textDim, fontSize: 13 }}>기술지표 데이터가 없습니다</div>
        )}
      </Card>

      {/* 적정가 분석 (Core) */}
      {m.fair_value && (canAccess(plan, "fair_value") ? (
        <Card style={{ marginBottom: 14, borderLeft: `3px solid ${T.accent}` }}>
          <SectionTitle icon="Target" tip="현재 EPS(주당순이익)와 PER 배수를 기반으로 적정 가격 범위를 추정합니다" badge="CORE">적정가 분석</SectionTitle>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
            <div style={{ textAlign: "center", padding: "8px 14px", background: T.bg, borderRadius: 8 }}>
              <div style={{ fontSize: 11, color: T.text, fontWeight: 600 }}>보수적 (PER {m.fair_value.per_band?.low})</div>
              <div style={{ fontSize: 18, fontWeight: 900, color: T.red }}>{m.fair_value.low?.toLocaleString()}원</div>
            </div>
            <div style={{ textAlign: "center", padding: "8px 14px", background: T.bg, borderRadius: 8, border: `1px solid ${T.accent}30` }}>
              <div style={{ fontSize: 11, color: T.text, fontWeight: 600 }}>적정 (PER {m.fair_value.per_band?.mid})</div>
              <div style={{ fontSize: 18, fontWeight: 900, color: T.accent }}>{m.fair_value.mid?.toLocaleString()}원</div>
            </div>
            <div style={{ textAlign: "center", padding: "8px 14px", background: T.bg, borderRadius: 8 }}>
              <div style={{ fontSize: 11, color: T.text, fontWeight: 600 }}>낙관적 (PER {m.fair_value.per_band?.high})</div>
              <div style={{ fontSize: 18, fontWeight: 900, color: T.green }}>{m.fair_value.high?.toLocaleString()}원</div>
            </div>
          </div>
          {/* 위치 바 */}
          <div style={{ marginBottom: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: T.textMuted, marginBottom: 4 }}>
              <span>{m.fair_value.low?.toLocaleString()}</span>
              <span>현재가 {m.close?.toLocaleString()}원</span>
              <span>{m.fair_value.high?.toLocaleString()}</span>
            </div>
            <div style={{ position: "relative", height: 8, background: `linear-gradient(to right, ${T.green}40, ${T.accent}40, ${T.red}40)`, borderRadius: 4 }}>
              <div style={{ position: "absolute", left: `${m.fair_value.position}%`, top: -3, width: 14, height: 14, borderRadius: "50%", background: T.accent, border: `2px solid ${T.bg}`, transform: "translateX(-50%)" }} />
            </div>
          </div>
          <div style={{ fontSize: 12, color: T.text, padding: "8px 12px", background: T.bg, borderRadius: 8 }}>
            {m.fair_value.verdict === "저평가 구간" && `현재가가 적정 범위 아래에 있습니다. 저평가 가능성이 있지만, 이유가 있을 수 있으니 확인이 필요합니다.`}
            {m.fair_value.verdict === "저평가 근접" && `현재가가 적정 범위 하단에 가깝습니다. 매수 관점에서 관심 구간일 수 있습니다.`}
            {m.fair_value.verdict === "적정 범위" && `현재가가 적정 범위 안에 있습니다. 특별한 과열이나 저평가 신호는 없습니다.`}
            {m.fair_value.verdict === "고평가 근접" && `현재가가 적정 범위 상단에 가까워요. 추가매수는 신중하게 판단하세요.`}
            {!m.fair_value.verdict && "적정가 분석은 참고 범위입니다."}
            {m.fair_value.verdict === "고평가 구간" && `현재가가 적정 범위를 넘어섰습니다. 시장이 높은 성장을 기대하고 있지만, 기대에 못 미치면 조정 가능성이 있습니다.`}
          </div>
        </Card>
      ) : (
        <Card style={{ marginBottom: 14, cursor: "pointer" }} onClick={() => onUpgrade?.("fair_value")}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0" }}>
            <Lock size={20} color={T.textMuted} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 800, color: T.text }}>적정가 분석</div>
              <div style={{ fontSize: 11, color: T.textMuted }}>Core 플랜에서 PER 기반 적정가를 확인할 수 있습니다</div>
            </div>
            <span style={{ fontSize: 10, padding: "3px 8px", background: T.accent + "20", color: T.accent, borderRadius: 4, fontWeight: 700, marginLeft: "auto" }}>CORE</span>
          </div>
        </Card>
      ))}

      {/* VaR 리스크 시뮬레이션 (Core) */}
      {m.var_95 && (canAccess(plan, "var_analysis") ? (
        <Card style={{ marginBottom: 14, borderLeft: `3px solid ${T.red}` }}>
          <SectionTitle tip="과거 데이터를 기반으로 하루에 최대 얼마나 잃을 수 있는지 추정합니다. 95%는 20일 중 19일은 이 범위 안이라는 뜻입니다" badge="CORE">리스크 시뮬레이션</SectionTitle>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
            <div style={{ padding: "10px 14px", background: T.bg, borderRadius: 8 }}>
              <div style={{ fontSize: 11, color: T.text, fontWeight: 600 }}>95% VaR (하루 최대 손실)</div>
              <div style={{ fontSize: 20, fontWeight: 900, color: T.red }}>{m.var_95.var95_pct}%</div>
              <div style={{ fontSize: 13, color: T.red, fontWeight: 700 }}>약 -{m.var_95.var95_amount?.toLocaleString()}원</div>
            </div>
            <div style={{ padding: "10px 14px", background: T.bg, borderRadius: 8 }}>
              <div style={{ fontSize: 11, color: T.text, fontWeight: 600 }}>99% VaR (극단적 하루)</div>
              <div style={{ fontSize: 20, fontWeight: 900, color: T.red }}>{m.var_95.var99_pct}%</div>
              <div style={{ fontSize: 13, color: T.red, fontWeight: 700 }}>약 -{m.var_95.var99_amount?.toLocaleString()}원</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 16, marginBottom: 10, fontSize: 11, color: T.textDim }}>
            <span>일평균 수익률: {m.var_95.daily_mean > 0 ? "+" : ""}{m.var_95.daily_mean}%</span>
            <span>일 변동성: ±{m.var_95.daily_std}%</span>
            <span>분석 기간: {m.var_95.data_days}일</span>
          </div>
          <div style={{ fontSize: 12, color: T.text, padding: "8px 12px", background: T.bg, borderRadius: 8 }}>
            과거 {m.var_95.data_days}일 데이터 기준, 하루 최대 손실이 <span style={{ color: T.red, fontWeight: 700 }}>{m.var_95.var95_amount?.toLocaleString()}원</span>을 넘을 확률은 5%입니다.
            {m.var_95.daily_std > 3 ? " 변동성이 높은 구간이니 평소보다 주의가 필요합니다." : " 현재 변동성은 보통 수준입니다."}
          </div>
        </Card>
      ) : (
        <Card style={{ marginBottom: 14, cursor: "pointer" }} onClick={() => onUpgrade?.("var_analysis")}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0" }}>
            <Lock size={20} color={T.textMuted} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 800, color: T.text }}>리스크 시뮬레이션</div>
              <div style={{ fontSize: 11, color: T.textMuted }}>Core 플랜에서 VaR 기반 최대 손실 추정을 확인할 수 있습니다</div>
            </div>
            <span style={{ fontSize: 10, padding: "3px 8px", background: T.accent + "20", color: T.accent, borderRadius: 4, fontWeight: 700, marginLeft: "auto" }}>CORE</span>
          </div>
        </Card>
      ))}

      {/* 주가 변화 추이 차트 */}
      <TrendCharts symbol={holding.symbol} token={token} micro={m} />
    </div>
  );
}
